import {
  collection, doc, getDoc, getDocs, query, where, orderBy, addDoc,
  updateDoc, serverTimestamp, Timestamp
} from 'firebase/firestore';
import { db, COLLECTIONS } from './config';
import { localToUTC, detectTimezone } from '../utils/timezone';

// ------------------------------------------------------------------
// Internal helpers
// ------------------------------------------------------------------

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Sort bookings chronologically, preferring the UTC timestamp when present.
const sortByTime = (bookings) =>
  bookings.sort((a, b) => {
    const dateA = a.startAtUTC?.toDate?.() ?? new Date(`${a.date}T${a.startTime}`);
    const dateB = b.startAtUTC?.toDate?.() ?? new Date(`${b.date}T${b.startTime}`);
    return dateA - dateB;
  });

// ------------------------------------------------------------------
// Create a booking
// ------------------------------------------------------------------
export const createBooking = async (bookingData) => {
  try {
    // The student's timezone is required so we can store accurate UTC times.
    const timezone = bookingData.timezone || detectTimezone();

    const startUTC = localToUTC(bookingData.date, bookingData.startTime, timezone);
    const endUTC   = localToUTC(bookingData.date, bookingData.endTime,   timezone);

    const bookingRef = await addDoc(collection(db, COLLECTIONS.BOOKINGS), {
      studentId:       bookingData.studentId,
      teacherId:       bookingData.teacherId,
      studentName:     bookingData.studentName,
      teacherName:     bookingData.teacherName,
      studentEmail:    bookingData.studentEmail || '',
      // Legacy local-time fields kept for display fallback on old clients
      date:            bookingData.date,
      startTime:       bookingData.startTime,
      endTime:         bookingData.endTime,
      // UTC source-of-truth timestamps
      startAtUTC:      Timestamp.fromDate(startUTC),
      endAtUTC:        Timestamp.fromDate(endUTC),
      studentTimezone: timezone,
      duration:        bookingData.duration || 50,
      price:           bookingData.price,
      currency:        bookingData.currency || 'USD',
      language:        bookingData.language,
      isTrial:         bookingData.isTrial || false,
      status:          'pending',
      paymentStatus:   'pending',
      zoomLink:        null,
      notes:           bookingData.notes || '',
      reminderSent:    false,
      createdAt:       serverTimestamp(),
      updatedAt:       serverTimestamp(),
    });

    return { id: bookingRef.id };
  } catch (error) {
    console.error('Error creating booking:', error);
    throw error;
  }
};

// ------------------------------------------------------------------
// Get bookings for a teacher
// ------------------------------------------------------------------
export const getTeacherBookings = async (teacherId, status = null) => {
  try {
    const constraints = [where('teacherId', '==', teacherId)];
    if (status) constraints.push(where('status', '==', status));

    const snapshot = await getDocs(query(collection(db, COLLECTIONS.BOOKINGS), ...constraints));
    return sortByTime(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  } catch (error) {
    console.error('Error getting teacher bookings:', error);
    throw error;
  }
};

// ------------------------------------------------------------------
// Get bookings for a student
// ------------------------------------------------------------------
export const getStudentBookings = async (studentId, status = null) => {
  try {
    const constraints = [where('studentId', '==', studentId)];
    if (status) constraints.push(where('status', '==', status));

    const snapshot = await getDocs(query(collection(db, COLLECTIONS.BOOKINGS), ...constraints));
    return sortByTime(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
  } catch (error) {
    console.error('Error getting student bookings:', error);
    throw error;
  }
};

// ------------------------------------------------------------------
// Confirm a booking (teacher confirms)
// ------------------------------------------------------------------
export const confirmBooking = async (bookingId) => {
  try {
    const bookingRef = doc(db, COLLECTIONS.BOOKINGS, bookingId);
    const meetingId  = Math.random().toString(36).substring(2, 12);
    const zoomLink   = `https://meet.jit.si/EduSession-${meetingId}`;

    await updateDoc(bookingRef, {
      status:    'confirmed',
      zoomLink,
      updatedAt: serverTimestamp(),
    });

    return { zoomLink };
  } catch (error) {
    console.error('Error confirming booking:', error);
    throw error;
  }
};

// ------------------------------------------------------------------
// Cancel a booking
// ------------------------------------------------------------------
export const cancelBooking = async (bookingId, reason = '') => {
  try {
    await updateDoc(doc(db, COLLECTIONS.BOOKINGS, bookingId), {
      status:             'cancelled',
      cancellationReason: reason,
      cancelledAt:        serverTimestamp(),
      updatedAt:          serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error cancelling booking:', error);
    throw error;
  }
};

// ------------------------------------------------------------------
// Complete a booking (after lesson is done)
// ------------------------------------------------------------------
export const completeBooking = async (bookingId) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.BOOKINGS, bookingId), {
      status:      'completed',
      completedAt: serverTimestamp(),
      updatedAt:   serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error completing booking:', error);
    throw error;
  }
};

// ------------------------------------------------------------------
// Get upcoming lessons for a user
// ------------------------------------------------------------------
export const getUpcomingLessons = async (userId, role) => {
  try {
    const field = role === 'teacher' ? 'teacherId' : 'studentId';
    const now   = new Date();
    const today = toLocalDateStr(now);

    const snapshot = await getDocs(
      query(
        collection(db, COLLECTIONS.BOOKINGS),
        where(field, '==', userId),
        where('status', 'in', ['confirmed', 'pending'])
      )
    );

    const bookings = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(b => {
        // Prefer UTC timestamp; fall back to legacy date string for old documents.
        if (b.startAtUTC) return b.startAtUTC.toDate() >= now;
        return b.date >= today;
      });

    return sortByTime(bookings);
  } catch (error) {
    console.error('Error getting upcoming lessons:', error);
    throw error;
  }
};

// ------------------------------------------------------------------
// Get teacher's booked students (unique)
// ------------------------------------------------------------------
export const getTeacherStudentsList = async (teacherId) => {
  try {
    const snapshot = await getDocs(
      query(
        collection(db, COLLECTIONS.BOOKINGS),
        where('teacherId', '==', teacherId),
        where('status', 'in', ['confirmed', 'completed'])
      )
    );

    const studentMap = new Map();
    snapshot.docs.forEach(d => {
      const data = d.data();
      if (!studentMap.has(data.studentId)) {
        studentMap.set(data.studentId, {
          id:           data.studentId,
          name:         data.studentName,
          lastLesson:   data.date,
          totalLessons: 1,
        });
      } else {
        const existing = studentMap.get(data.studentId);
        existing.totalLessons += 1;
        if (data.date > existing.lastLesson) existing.lastLesson = data.date;
      }
    });

    return Array.from(studentMap.values());
  } catch (error) {
    console.error('Error getting teacher students:', error);
    throw error;
  }
};
